import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import smoothscroll from 'smoothscroll-polyfill';

import { showErrorBar } from 'actions/errorBar';
import { revisitFeatureTour } from 'actions/feature-tour';
import {
  toggleGoogleInviteModal,
  toggleGoogleOAuthError,
} from 'actions/google';
import {
  updateFromPublicSharePage,
  updateTheaterMode,
} from 'actions/videoPlayer';
import { ERROR } from 'constants/error-severities';
import {
  ROLLOUT_WEBSITE_RECORDING,
  SEND_HLS_PLAYBACK_STATS,
} from 'constants/featureFlag';
import { NOT_FOUND } from 'constants/routes';
import { VIDEO } from 'constants/ssr';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import { initVideoCreator } from 'creators/shareVideo';
import { getUserCreator } from 'creators/user';
import sentryActionState from 'middleware/sentry';
import { parseCookie } from 'utilities/cookie';
import { injectCustomBrandColors } from 'utilities/custom-branding';
import * as logger from 'utilities/logger';
import * as loggerx from 'utilities/loggerx';
import { processPushEventData } from 'utilities/notifications';
import * as storeUtil from 'utilities/store';
import { getParam } from 'utilities/url';

import reducers from 'reducers';

import { UrlQueryParamContextWrapper } from './contexts/UrlQueryParamContext';
import { wrapGraphQLProvider } from './utilities/graphql';
import { KEY_THEATER_MODE } from '../../shared/constants/cookie';
import { GOOGLE_APP } from '../../shared/constants/oAuthKeys';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import ShareVideo from 'components/share-video';

// 3rd-party
import 'css/medium.css';

const WebRecordingUtility = React.lazy(() =>
  import('components/web-recording-utility')
);

// we do this immediately in case someone tries to copy the link with the
// query param in it right after a recording
const focusTitleParam = getParam('focus_title') === '1';

const mutedParam = getParam('muted') === '1';

const timeParam = getParam('t');

const oauthError = getParam('oauth_error');

const provider = getParam('provider');

const data = getParam('data') ? JSON.parse(atob(getParam('data'))) : {};

const isGoogleApp = provider === GOOGLE_APP;

// init custom logger asap
logger.init();

function init() {
  const hasParamToClear = [
    focusTitleParam,
    mutedParam,
    oauthError,
    provider,
  ].find(Boolean);

  // the pathname gets cleared inside react after the error bar has been displayed
  if (hasParamToClear || !isEmpty(data)) {
    window.history.replaceState('', '', window.location.pathname);
  }

  if (data.referrer === 'push') {
    processPushEventData(data);
  }

  smoothscroll.polyfill();

  const ExceptionsTracker = loggerx.initTracker();
  const middleware = [thunk];

  if (ExceptionsTracker) {
    middleware.push(sentryActionState(ExceptionsTracker));
  }

  const store = storeUtil.init(reducers, middleware);
  const container = document.getElementById('container');

  handleOauthProvider(store);
  store.dispatch(updateFromPublicSharePage(true));
  store.dispatch(getUserCreator());
  store.dispatch(initVideoCreator(window[VIDEO]));
  store.dispatch(getFeatureFlagCreator(SEND_HLS_PLAYBACK_STATS));

  initElevioHook(store);

  injectCustomBrandColors();

  if (window[VIDEO] && !window[VIDEO].current_user_is_owner) {
    const lastTheaterMode =
      parseCookie(document.cookie)[KEY_THEATER_MODE] === 'true';

    if (lastTheaterMode) {
      store.dispatch(updateTheaterMode(true));
    }
  }

  // set store reference on window so the video iframe can refer to it
  window.reduxStore = store;

  render(
    wrapGraphQLProvider(
      <Provider store={store}>
        <UrlQueryParamContextWrapper>
          <ShareVideo
            focusTitle={focusTitleParam}
            mutedParam={mutedParam}
            timeParam={timeParam}
          />
          <FeatureFlaggedComponent
            flag={ROLLOUT_WEBSITE_RECORDING}
            activationValue={true}
          >
            <React.Suspense fallback={<div />}>
              <WebRecordingUtility />
            </React.Suspense>
          </FeatureFlaggedComponent>
        </UrlQueryParamContextWrapper>
      </Provider>
    ),
    container
  );
}

function initElevioHook(store) {
  window._elev || (window._elev = {});

  window._elev.events = {
    moduleOpened(id) {
      const ELEVIO_CUSTOM_TRIGGER_ID = 12;

      if (Number(id) === ELEVIO_CUSTOM_TRIGGER_ID) {
        store.dispatch(revisitFeatureTour());
      }
    },
  };
}

function handleOauthProvider(store) {
  if (isGoogleApp) {
    store.dispatch(toggleGoogleInviteModal(true));
    oauthError && store.dispatch(toggleGoogleOAuthError(true));

    return;
  }

  oauthError &&
    store.dispatch(showErrorBar(ERROR, decodeURIComponent(oauthError)));
}

try {
  window[VIDEO] && init();
} catch (e) {
  loggerx.fatal(
    e,
    {
      message: 'Share Video page',
      hasVideo: Boolean(window?.[VIDEO]),
    },
    { team: 'create' }
  );
}

// video does not exist anymore
if (!window[VIDEO]) {
  window.location.href = NOT_FOUND;
}
